import { normalizeOrg } from 'js/utils/org-utils';

export const ORG_WEIGHT_SCHEME_EQUAL = 'equal';
export const ORG_WEIGHT_SCHEME_TEAM_COUNT = 'team_count';
export const ORG_WEIGHT_SCHEME_CUSTOM = 'custom';

export const DEFAULT_ORG_WEIGHT_SCHEME = ORG_WEIGHT_SCHEME_TEAM_COUNT;

export const ORG_WEIGHT_SCHEME_OPTIONS = [
  {
    value: ORG_WEIGHT_SCHEME_TEAM_COUNT,
    label: 'By team count',
    tooltip:
      'Each group is weighted by the total number of teams beneath it (default).',
  },
  {
    value: ORG_WEIGHT_SCHEME_EQUAL,
    label: 'Equal weights',
    tooltip: 'Every group is weighted equally when rolling up scores.',
  },
  {
    value: ORG_WEIGHT_SCHEME_CUSTOM,
    label: 'Custom weights',
    tooltip:
      'Set explicit weights per group and team. Defaults to the sum of the weights under each group.',
  },
];

function normalizeWeight(value) {
  if (value === '' || value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
}

/** Canonical root org id for weight keys (from API when tree root ≠ DB root). */
export function weightsRootOrganizationId(organization) {
  return (
    organization?.weights_root_organization_id ??
    organization?.weightsRootOrganizationId ??
    organization?.id
  );
}

/** Top-level org in the weights tree (not the same as `is_root` on the record). */
export function isTreeRootWeightRow(row) {
  return row?.type === 'org' && (row.pathToNode?.length ?? 0) === 0;
}

/** Only the tree root has no weight; sub-orgs and teams do. */
export function isEditableWeightRow(row) {
  return !isTreeRootWeightRow(row);
}

/** Stable key for draft override map entries (excludes root org). */
export function orgWeightRowKey(rootOrgId, parentOrgId, node) {
  if (!rootOrgId || !node || !isEditableWeightRow(node)) return '';
  if (parentOrgId == null) return '';
  if (node.type === 'team') {
    return `team:${rootOrgId}:${parentOrgId}:${node.id}`;
  }
  if (node.type === 'org') {
    return `child:${rootOrgId}:${parentOrgId}:${node.id}`;
  }
  return '';
}

/** Key for computed rollups including root (not persisted as custom override). */
export function orgWeightResolutionKey(rootOrgId, parentOrgId, node) {
  if (!rootOrgId || !node) return '';
  if (isTreeRootWeightRow(node)) {
    return `root:${rootOrgId}`;
  }
  return orgWeightRowKey(rootOrgId, parentOrgId, node);
}

export function parentOrgIdForRow(row) {
  const path = row.pathToNode ?? [];
  const parent = path[path.length - 1];
  return parent?.type === 'org' ? parent.id : null;
}

function findOrgWeightRow(organization, { parentOrgId, orgId, teamId }) {
  if (parentOrgId == null) return undefined;

  return (organization?.org_weights ?? []).find((entry) => {
    if (entry.parent_organization_id !== parentOrgId) return false;
    if (teamId != null) {
      return entry.team_id === teamId && entry.organization_id == null;
    }
    if (orgId != null) {
      return entry.organization_id === orgId && entry.team_id == null;
    }
    return false;
  });
}

export function normalizeOrgWeightOverrides(weights) {
  if (!weights || typeof weights !== 'object' || Array.isArray(weights)) {
    return {};
  }
  const out = {};
  for (const [k, v] of Object.entries(weights)) {
    const key = String(k ?? '').trim();
    if (!key) continue;
    const nw = normalizeWeight(v);
    if (nw !== null) out[key] = nw;
  }
  return out;
}

export function normalizeOrgWeightConfig(config) {
  const scheme = config?.scheme ?? DEFAULT_ORG_WEIGHT_SCHEME;
  const validScheme = ORG_WEIGHT_SCHEME_OPTIONS.some((o) => o.value === scheme)
    ? scheme
    : DEFAULT_ORG_WEIGHT_SCHEME;
  return {
    scheme: validScheme,
    weights: normalizeOrgWeightOverrides(config?.weights),
  };
}

function rowOverrideFromDb(organization, row) {
  const parentId = parentOrgIdForRow(row);

  if (!isEditableWeightRow(row) || parentId == null) {
    return null;
  }

  if (row.type === 'team') {
    return (
      findOrgWeightRow(organization, {
        parentOrgId: parentId,
        teamId: row.id,
      })?.weight ?? null
    );
  }

  if (row.type === 'org') {
    return (
      findOrgWeightRow(organization, {
        parentOrgId: parentId,
        orgId: row.id,
      })?.weight ?? null
    );
  }

  return null;
}

/** Reads weight scheme + overrides from an organization record (root org). */
export function getOrgWeightConfig(organization) {
  const scheme = organization?.weight_scheme ?? DEFAULT_ORG_WEIGHT_SCHEME;

  if (!organization?.id) {
    return normalizeOrgWeightConfig({ scheme });
  }

  const tree = normalizeOrgForWeights(organization);
  const rows = tree ? flattenOrgTreeForWeights(tree) : [];
  const weights = {};

  for (const row of rows) {
    if (!isEditableWeightRow(row)) continue;
    const key = orgWeightRowKey(
      weightsRootOrganizationId(organization),
      parentOrgIdForRow(row),
      row
    );
    const value = rowOverrideFromDb(organization, row);
    if (key && value != null) {
      weights[key] = value;
    }
  }

  return normalizeOrgWeightConfig({ scheme, weights });
}

export function rowToApiWeightEntry(organization, row, weightValue) {
  if (!isEditableWeightRow(row)) return null;

  const parentId = parentOrgIdForRow(row);
  const entry = { weight: weightValue };

  if (row.type === 'team') {
    if (parentId == null) return null;
    return {
      ...entry,
      parent_organization_id: parentId,
      team_id: row.id,
    };
  }

  if (row.type === 'org') {
    if (parentId == null) return null;
    return {
      ...entry,
      parent_organization_id: parentId,
      organization_id: row.id,
    };
  }

  return null;
}

function overrideToApiRows(draftConfig, committedConfig, organization) {
  const config = normalizeOrgWeightConfig(draftConfig);
  const committed = normalizeOrgWeightConfig(committedConfig);
  const tree = normalizeOrgForWeights(organization);
  const rows = tree ? flattenOrgTreeForWeights(tree) : [];
  const orgWeights = [];

  for (const row of rows) {
    if (!isEditableWeightRow(row)) continue;
    const key = orgWeightRowKey(
      weightsRootOrganizationId(organization),
      parentOrgIdForRow(row),
      row
    );
    if (!key) continue;

    const weight = Object.prototype.hasOwnProperty.call(config.weights, key)
      ? config.weights[key]
      : null;
    const previous = Object.prototype.hasOwnProperty.call(
      committed.weights,
      key
    )
      ? committed.weights[key]
      : null;

    if (weight === previous) continue;

    const entry = rowToApiWeightEntry(organization, row, weight ?? null);
    if (entry) orgWeights.push(entry);
  }

  const payload = { weight_scheme: config.scheme };

  if (orgWeights.length > 0) {
    payload.org_weights = orgWeights;
  }

  return payload;
}

export function weightConfigPayload(
  draftConfig,
  organization,
  committedConfig
) {
  return overrideToApiRows(draftConfig, committedConfig, organization);
}

export function weightSchemePayload(scheme) {
  return { weight_scheme: scheme };
}

export function parseWeightInput(rawValue) {
  if (rawValue === '' || rawValue == null) return null;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** Whether two stored override values are the same (null/empty equivalent). */
export function orgWeightValuesEqual(a, b) {
  const left = a == null || a === '' ? null : Number(a);
  const right = b == null || b === '' ? null : Number(b);
  if (left == null && right == null) return true;
  if (left == null || right == null) return false;
  return left === right;
}

export function persistedWeightForRow(organization, row) {
  const key = orgWeightRowKey(
    weightsRootOrganizationId(organization),
    parentOrgIdForRow(row),
    row
  );
  if (!key) return null;
  const value = rowOverrideFromDb(organization, row);
  return value ?? null;
}

/** API payload for a single custom weight row change. */
export function singleOrgWeightPayload(organization, row, rawValue) {
  const entry = rowToApiWeightEntry(
    organization,
    row,
    parseWeightInput(rawValue)
  );
  if (!entry) return null;
  return { org_weights: [entry] };
}

/** Stable key for org tree shape (ignores weight data) so live edits are not reset on save. */
export function orgStructureSyncKey(organization) {
  if (!organization?.id) return null;

  const walk = (node) => ({
    id: node.id,
    type: node.is_root != null || node.child_orgs != null ? 'org' : 'team',
    children: [
      ...(node.child_orgs ?? []).map((child) => walk(child)),
      ...(node.teams ?? []).map((team) => ({
        id: team.id,
        type: 'team',
        children: [],
      })),
    ],
  });

  return `${organization.id}:${JSON.stringify(walk(organization))}`;
}

/** Count of team nodes in subtree (orgs/groups excluded). */
export function countTeamsInSubtree(node) {
  if (node.type === 'team') return 1;
  return (node.children ?? []).reduce(
    (sum, child) => sum + countTeamsInSubtree(child),
    0
  );
}

/** Pre-order flatten of normalized tree for weight grid rows (includes ancestor path for gutters). */
export function flattenOrgTreeForWeights(rootNode) {
  const rows = [];
  const walk = (node, pathToNode) => {
    rows.push({ ...node, pathToNode });
    for (const child of node.children ?? []) {
      walk(child, [...pathToNode, node]);
    }
  };
  if (rootNode) {
    walk(rootNode, []);
  }
  return rows;
}

export function normalizeOrgForWeights(organization) {
  if (!organization) return null;
  return normalizeOrg(organization, 'org', 0, '');
}

/**
 * Resolves effective weight per row key (bottom-up).
 * Returns Map<rowKey, number> and Map<rowKey, defaultWeight> for placeholders.
 */
export function resolveOrgWeights(
  rootNode,
  scheme,
  overrides = {},
  organization
) {
  const normalizedOverrides = normalizeOrgWeightOverrides(overrides);
  const effectiveByKey = new Map();
  const defaultByKey = new Map();

  const resolveNode = (node, pathToNode) => {
    const row = { ...node, pathToNode };
    const rowKey = orgWeightResolutionKey(
      weightsRootOrganizationId(organization),
      parentOrgIdForRow(row),
      row
    );

    const childWeights = (node.children ?? []).map((child) => {
      resolveNode(child, [...pathToNode, node]);
      const childRow = {
        ...child,
        pathToNode: [...pathToNode, node],
      };
      const childKey = orgWeightResolutionKey(
        weightsRootOrganizationId(organization),
        parentOrgIdForRow(childRow),
        childRow
      );
      return effectiveByKey.get(childKey);
    });

    const defaultWeight = computeDefaultWeight(node, scheme, childWeights);
    defaultByKey.set(rowKey, defaultWeight);

    let effective = defaultWeight;
    if (scheme === ORG_WEIGHT_SCHEME_CUSTOM) {
      const override = normalizedOverrides[rowKey];
      if (override != null) {
        effective = override;
      }
    }

    effectiveByKey.set(rowKey, effective);
    return effective;
  };

  if (rootNode) {
    resolveNode(rootNode, []);
  }

  return { effectiveByKey, defaultByKey };
}

function computeDefaultWeight(node, scheme, childWeights) {
  if (scheme === ORG_WEIGHT_SCHEME_EQUAL) {
    return 1;
  }

  if (scheme === ORG_WEIGHT_SCHEME_TEAM_COUNT) {
    return countTeamsInSubtree(node);
  }

  if (node.type === 'team') return 1;
  const weights = childWeights.filter((w) => w != null);
  return weights.length ? weights.reduce((a, b) => a + b, 0) : 0;
}

export function defaultWeightPlaceholderString(defaultWeight) {
  if (defaultWeight == null || !Number.isFinite(defaultWeight)) return '';
  return String(defaultWeight);
}

export function overrideMapControlledInputValue(overrideValue) {
  if (overrideValue === undefined || overrideValue === null) return '';
  return String(overrideValue);
}

export function normalizeOrgWeightSectionForCompare(config) {
  const normalized = normalizeOrgWeightConfig(config);
  const weights = Object.entries(normalized.weights)
    .map(([key, weight]) => ({ key, weight }))
    .sort((a, b) => a.key.localeCompare(b.key));
  return {
    scheme: normalized.scheme,
    weights,
  };
}

export function orgWeightSectionEqual(a, b) {
  if (a.scheme !== b.scheme) return false;
  if (a.weights.length !== b.weights.length) return false;
  return a.weights.every(
    (row, i) =>
      row.key === b.weights[i]?.key && row.weight === b.weights[i]?.weight
  );
}
